import { Alerts } from 'constants/alerts';
import { Resources } from 'constants/resources';

import { useMemo, useState } from 'react';

import { useApiContext } from 'context/apiContext';
import { usePermissions } from 'hooks/usePermissions';
import { useRecordModal } from 'hooks/useRecordModal';
import { MerchantShowCommissionsCreateDialog } from 'pages/Merchants/MerchantsShow/MerchantShowCommissions/MerchantShowCommissionsCreateDialog/MerchantShowCommissionsCreateDialog';
import { NewVersionCommission } from 'pages/Merchants/MerchantsShow/MerchantShowCommissions/MerchantShowCommissionsCreateNewVersionDialog/MerchantShowCommissionsCreateNewVersionDialog';
import { MerchantShowCommissionsCreateNewVersionDialog } from 'pages/Merchants/MerchantsShow/MerchantShowCommissions/MerchantShowCommissionsCreateNewVersionDialog/MerchantShowCommissionsCreateNewVersionDialog';
import { MerchantCommissionsDeleteDialog } from 'pages/Merchants/MerchantsShow/MerchantShowCommissions/MerchantShowCommissionsDeleteDialog/MerchantCommissionsDeleteDialog';
import { MerchantShowCommissionsEditDialog } from 'pages/Merchants/MerchantsShow/MerchantShowCommissions/MerchantShowCommissionsEditDialog/MerchantShowCommissionsEditDialog';
import { MerchantShowCommissionsShowDialog } from 'pages/Merchants/MerchantsShow/MerchantShowCommissions/MerchantShowCommissionsShowDialog/MerchantShowCommissionsShowDialog';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { useListController, WithListContext } from 'react-admin';
import { FormProvider } from 'react-hook-form';
import { Alert } from 'shared/mui/Alert/Alert';
import { CreateButton } from 'shared/mui/Buttons';
import { ReferenceButton } from 'shared/mui/Buttons/ReferenceButton/ReferenceButton';
import { FilterContextProvider } from 'shared/mui/Filter/context/filterContext';
import {
  FilterApplyButton,
  FilterButton,
  FilterClearButton,
  FilterResetButton,
} from 'shared/mui/Filter/FilterButtons';
import { FilterColumnsButton } from 'shared/mui/Filter/FilterButtons';
import { FilterChipsToolbar } from 'shared/mui/Filter/FilterChipsToolbar/FilterChipsToolbar';
import {
  FilterDrawer,
  FilterDrawerActionsButtons,
  FilterDrawerContent,
} from 'shared/mui/Filter/FilterDrawer';
import { FilterToolbar } from 'shared/mui/Filter/FilterToolbar/FilterToolbar';
import { useFilters } from 'shared/mui/Filter/hooks/useFilters';
import { DatagridSkeleton } from 'shared/react-admin/Datagrids';
import { ListPage } from 'shared/react-admin/Pages';
import { CommissionsAgreeDialog } from 'shared/widgets/CommissionsAgreeDialog/CommissionsAgreeDialog';
import { CommissionsApproveRefundSuccessDialog } from 'shared/widgets/CommissionsApproveRefundSuccessDialog/CommissionsApproveRefundSuccessDialog';
import { CommissionsArchivedDialog } from 'shared/widgets/CommissionsArchivedDialog/CommissionsArchivedDialog';
import { CommissionsArchivedErrorDialog } from 'shared/widgets/CommissionsArchivedErrorDialog/CommissionsArchivedErrorDialog';
import { CommissionsByAgreementDialog } from 'shared/widgets/CommissionsByAgreementDialog/CommissionsByAgreementDialog';
import { CommissionsCancelDialog } from 'shared/widgets/CommissionsCancelDialog/CommissionsCancelDialog';
import { CommissionsCreateApprovedSuccessDialog } from 'shared/widgets/CommissionsCreateApprovedSuccessDialog/CommissionsCreateApprovedSuccessDialog';
import { CommissionsCreateSuccessDialog } from 'shared/widgets/CommissionsCreateSuccessDialog/CommissionsCreateSuccessDialog';
import { CommissionsImplementDialog } from 'shared/widgets/CommissionsImplementDialog/CommissionsImplementDialog';
import { CommissionsImplementErrorDialog } from 'shared/widgets/CommissionsImplementErrorDialog/CommissionsImplementErrorDialog';
import { CommissionsRefundSuccessDialog } from 'shared/widgets/CommissionsRefundSuccessDialog/CommissionsRefundSuccessDialog';
import { CommissionsRejectDialog } from 'shared/widgets/CommissionsRejectDialog/CommissionsRejectDialog';
import { Commission } from 'types/embedded/commission';
import { Permission } from 'types/embedded/permission';
import { cleanEmpty } from 'utils/cleanEmpty';

import { MerchantShowCommissionsListDatagrid } from './MerchantShowCommissionsListDatagrid/MerchantShowCommissionsListDatagrid';
import { MerchantShowCommissionsListFilterChips } from './MerchantShowCommissionsListFilterChips';
import { MerchantShowCommissionsListFilterValues } from './MerchantShowCommissionsListFilterValues';
import { MerchantShowCommissionsListReferenceDialog } from './MerchantShowCommissionsListReferenceDialog/MerchantShowCommissionsListReferenceDialog';
import { MerchantsShowActionSkeleton } from '../../components/MerchantsShowActionSkeleton';
import { MerchantsShowFiltersSkeleton } from '../../components/MerchantsShowFiltersSkeleton';
import { CommissionCreateTargetStatus } from '../constants/commissionCreateTargetStatus';
import { CommissionCreateResult } from '../MerchantShowCommissionsCreateDialog/MerchantShowCommissionsCreateDialog';

export type MerchantShowCommissionsFormValues = {
  name: string;
  status: string[];
  provider: string[];
  currency: Record<string, any>;
  id: string;
  transactionTypes: string[];
  parent: { id: string | null };
};

export const MERCHANT_COMMISSIONS_PREFERENCE_KEY = 'merchant.commissions.list.datagrid';

export const DEFAULT_VALUES = {
  name: '',
  status: [],
  provider: [],
  currency: {
    id: [],
  },
  id: '',
  transactionTypes: [],
  parent: { id: null },
};

type Props = {
  merchantId?: string;
  merchantName?: string;
};

export const MerchantShowCommissionsList: React.FC<Props> = ({ merchantId }) => {
  const translate = useTypedTranslate();
  const { isLoading } = useListController();

  const { user } = useApiContext();
  const {
    form,
    appliedFilters,
    setAppliedFilters,
    setAppliedFilter,
    onSubmit,
    onReset,
    openDrawer,
    toggleDrawer,
  } = useFilters<MerchantShowCommissionsFormValues>({
    defaultValues: DEFAULT_VALUES,
    resetValues: DEFAULT_VALUES,
  });

  const [openReferenceDialog, setOpenReferenceDialog] = useState<boolean>(false);
  const { hasResourcePermissions } = usePermissions();
  const [openCreateDialog, setOpenCreateDialog] = useState(false);

  const editModal = useRecordModal<Commission>();
  const showModal = useRecordModal<Commission>();
  const deleteModal = useRecordModal<Commission>();
  const agreementModal = useRecordModal<Commission>();
  const agreeModal = useRecordModal<Commission>();
  const rejectModal = useRecordModal<Commission>();
  const implementModal = useRecordModal<Commission>();
  const cancelModal = useRecordModal<Commission>();
  const archivedModal = useRecordModal<Commission>();
  const implementErrorModal = useRecordModal<Commission>();
  const archivedErrorModal = useRecordModal<Commission>();
  const newVersionModal = useRecordModal<NewVersionCommission>();
  const createSuccessModal = useRecordModal<Commission>();
  const createApprovedSuccessModal = useRecordModal<Commission>();
  const newVersionSuccessModal = useRecordModal<Commission>();
  const createRefundSuccessModal = useRecordModal<Commission>();
  const createApprovedRefundSuccessModal = useRecordModal<Commission>();

  const listFilters = useMemo(() => {
    return cleanEmpty({
      ...appliedFilters,
      merchant: merchantId,
      ignoreParentFilter:
        !!appliedFilters?.parent?.id || !!appliedFilters?.status?.length || !!appliedFilters?.name,
    });
  }, [appliedFilters, merchantId]);

  const isListFilterExist = Object.keys(cleanEmpty(appliedFilters)).length > 0;

  const handleCreateSuccess = ({
    data,
    targetStatus,
    withRefundAndChargeback,
  }: CommissionCreateResult) => {
    const isApproved = targetStatus === CommissionCreateTargetStatus.Approved;

    if (isApproved && withRefundAndChargeback) {
      createApprovedRefundSuccessModal.handleOpenRecordModal(data);
      return;
    }
    if (isApproved) {
      createApprovedSuccessModal.handleOpenRecordModal(data);
      return;
    }
    if (withRefundAndChargeback) {
      createRefundSuccessModal.handleOpenRecordModal(data);
      return;
    }
    createSuccessModal.handleOpenRecordModal(data);
  };

  return (
    <ListPage
      actions={[
        isLoading ? (
          <MerchantsShowActionSkeleton />
        ) : (
          <ReferenceButton key="reference-btn" onClick={() => setOpenReferenceDialog(true)} />
        ),
        hasResourcePermissions(Resources.Commissions, [Permission.Full, Permission.Create]) &&
          (isLoading ? (
            <MerchantsShowActionSkeleton />
          ) : (
            <CreateButton
              key="create-btn"
              onClick={(e) => {
                e.preventDefault();
                setOpenCreateDialog(true);
              }}
            />
          )),
      ]}
      disableSyncWithLocation
      empty={false}
      filter={listFilters}
      headerTitle={{
        titleText: translate('app.merchantCommissions.feesList'),
        titleSize: 'smallBold',
      }}
      resource="commissions"
      sort={{ field: 'id', order: 'DESC' }}
      sx={{ marginTop: 0, marginBottom: 2 }}
    >
      {isLoading ? (
        <MerchantsShowFiltersSkeleton />
      ) : (
        <>
          <FilterToolbar
            leftActionsSlot={[
              <FilterButton key="filter" onClick={toggleDrawer(true)} />,
              <FilterColumnsButton
                key="select-column"
                preferenceKey={MERCHANT_COMMISSIONS_PREFERENCE_KEY}
              />,
              <FilterClearButton
                key="clear"
                onClick={() => {
                  onReset();
                }}
                visible={isListFilterExist}
              />,
            ]}
          />
          <FilterDrawer onClose={toggleDrawer(false)} open={openDrawer}>
            <FormProvider {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)}>
                <FilterDrawerContent>
                  <MerchantShowCommissionsListFilterValues />
                </FilterDrawerContent>
                <FilterDrawerActionsButtons>
                  <FilterResetButton label={translate('app.buttons.reset')} onReset={onReset} />
                  <FilterApplyButton type="submit" />
                </FilterDrawerActionsButtons>
              </form>
            </FormProvider>
          </FilterDrawer>
          <FilterChipsToolbar appliedFilters={appliedFilters} isSkipBooleanValues={false}>
            <FilterContextProvider
              appliedFilters={appliedFilters}
              setAppliedFilter={setAppliedFilter}
            >
              <FormProvider {...form}>
                <MerchantShowCommissionsListFilterChips />
              </FormProvider>
            </FilterContextProvider>
          </FilterChipsToolbar>
        </>
      )}
      <WithListContext
        render={({ data, isLoading }) => {
          if (isLoading) {
            return <DatagridSkeleton columnsCount={10} />;
          }

          if (user.alerts.includes(Alerts.EmptyProviderAccountAccess)) {
            return (
              <Alert severity="warning">
                {translate('app.merchantCommissions.emptyProviderAccount', {
                  firstName: user.firstName,
                  lastName: user.lastName,
                })}
              </Alert>
            );
          }

          if (user.alerts.includes(Alerts.EmptyMerchantAccess)) {
            return (
              <Alert severity="warning">
                {translate('app.merchantCommissions.emptyMerchantAccess', {
                  firstName: user.firstName,
                  lastName: user.lastName,
                })}
              </Alert>
            );
          }

          if (!data?.length && !isLoading) {
            return (
              <Alert severity="info">
                {translate('app.merchantCommissions.noFeesForAvailable')}
              </Alert>
            );
          }

          return (
            <FormProvider {...form}>
              <FilterContextProvider
                appliedFilters={appliedFilters}
                setAppliedFilter={setAppliedFilter}
              >
                <MerchantShowCommissionsListDatagrid
                  handleOpenDeleteDialog={deleteModal.handleOpenRecordModal}
                  handleOpenEditDialog={editModal.handleOpenRecordModal}
                  handleOpenShowDialog={showModal.handleOpenRecordModal}
                />
              </FilterContextProvider>
            </FormProvider>
          );
        }}
      />
      {!!merchantId && (
        <MerchantShowCommissionsCreateDialog
          merchantId={merchantId}
          onClose={() => setOpenCreateDialog(false)}
          onSuccess={handleCreateSuccess}
          open={openCreateDialog}
        />
      )}
      <CommissionsRefundSuccessDialog
        commissionName={createRefundSuccessModal.record?.name || ''}
        onAgreement={() => {
          createRefundSuccessModal.handleCloseRecordModal();
          showModal.handleOpenRecordModal(createRefundSuccessModal.record);
        }}
        onClose={createRefundSuccessModal.handleCloseRecordModal}
        open={createRefundSuccessModal.openRecordModal}
      />
      <CommissionsApproveRefundSuccessDialog
        commissionName={createApprovedRefundSuccessModal.record?.name || ''}
        onClose={createApprovedRefundSuccessModal.handleCloseRecordModal}
        open={createApprovedRefundSuccessModal.openRecordModal}
      />
      <CommissionsCreateSuccessDialog
        commissionName={createSuccessModal.record?.name || ''}
        onAgreement={() => {
          createSuccessModal.handleCloseRecordModal();
          showModal.handleOpenRecordModal(createSuccessModal.record);
        }}
        onClose={createSuccessModal.handleCloseRecordModal}
        open={createSuccessModal.openRecordModal}
      />
      <CommissionsCreateApprovedSuccessDialog
        commissionName={createApprovedSuccessModal.record?.name || ''}
        onClose={createApprovedSuccessModal.handleCloseRecordModal}
        open={createApprovedSuccessModal.openRecordModal}
      />
      {!!merchantId && (
        <MerchantShowCommissionsCreateNewVersionDialog
          merchantId={merchantId}
          onClose={newVersionModal.handleCloseRecordModal}
          onCreateApproved={createApprovedSuccessModal.handleOpenRecordModal}
          onCreateNew={newVersionSuccessModal.handleOpenRecordModal}
          open={newVersionModal.openRecordModal}
          record={newVersionModal.record}
        />
      )}
      <CommissionsCreateSuccessDialog
        commissionName={newVersionSuccessModal.record?.name || ''}
        onAgreement={() => {
          newVersionSuccessModal.handleCloseRecordModal();
          showModal.handleOpenRecordModal(newVersionSuccessModal.record);
        }}
        onClose={newVersionSuccessModal.handleCloseRecordModal}
        open={newVersionSuccessModal.openRecordModal}
      />
      <MerchantShowCommissionsEditDialog
        merchantId={merchantId}
        onClose={editModal.handleCloseRecordModal}
        open={editModal.openRecordModal}
        record={editModal.record}
      />
      <MerchantShowCommissionsShowDialog
        handleOpenModalAgree={(record) => {
          showModal.handleCloseRecordModal();
          agreeModal.handleOpenRecordModal(record);
        }}
        handleOpenModalAgreement={(record) => {
          showModal.handleCloseRecordModal();
          agreementModal.handleOpenRecordModal(record);
        }}
        handleOpenModalArchived={(record) => {
          showModal.handleCloseRecordModal();
          archivedModal.handleOpenRecordModal(record);
        }}
        handleOpenModalArchivedError={(record) => {
          showModal.handleCloseRecordModal();
          archivedErrorModal.handleOpenRecordModal(record);
        }}
        handleOpenModalCancel={(record) => {
          showModal.handleCloseRecordModal();
          cancelModal.handleOpenRecordModal(record);
        }}
        handleOpenModalImplement={(record) => {
          showModal.handleCloseRecordModal();
          implementModal.handleOpenRecordModal(record);
        }}
        handleOpenModalImplementError={(record) => {
          showModal.handleCloseRecordModal();
          implementErrorModal.handleOpenRecordModal(record);
        }}
        handleOpenModalNewVersion={(record) => {
          showModal.handleCloseRecordModal();
          newVersionModal.handleOpenRecordModal(record);
        }}
        handleOpenModalReject={(record) => {
          showModal.handleCloseRecordModal();
          rejectModal.handleOpenRecordModal(record);
        }}
        onClose={showModal.handleCloseRecordModal}
        onDelete={deleteModal.handleOpenRecordModal}
        onEdit={editModal.handleOpenRecordModal}
        open={showModal.openRecordModal}
        record={showModal.record}
        setAppliedFilters={setAppliedFilters}
        setValue={form.setValue}
      />
      <CommissionsByAgreementDialog
        onClose={agreementModal.handleCloseRecordModal}
        open={agreementModal.openRecordModal}
        record={agreementModal.record}
      />
      <CommissionsAgreeDialog
        onClose={agreeModal.handleCloseRecordModal}
        open={agreeModal.openRecordModal}
        record={agreeModal.record}
      />
      <CommissionsRejectDialog
        onClose={rejectModal.handleCloseRecordModal}
        open={rejectModal.openRecordModal}
        record={rejectModal.record}
      />
      <CommissionsImplementDialog
        onClose={implementModal.handleCloseRecordModal}
        open={implementModal.openRecordModal}
        record={implementModal.record}
      />
      <CommissionsCancelDialog
        onClose={cancelModal.handleCloseRecordModal}
        open={cancelModal.openRecordModal}
        record={cancelModal.record}
      />
      <CommissionsArchivedDialog
        onClose={archivedModal.handleCloseRecordModal}
        open={archivedModal.openRecordModal}
        record={archivedModal.record}
      />
      <CommissionsImplementErrorDialog
        onBackClick={implementErrorModal.handleCloseRecordModal}
        onClose={implementErrorModal.handleCloseRecordModal}
        open={implementErrorModal.openRecordModal}
        record={implementErrorModal.record}
      />
      <CommissionsArchivedErrorDialog
        onBackClick={archivedErrorModal.handleCloseRecordModal}
        onClose={archivedErrorModal.handleCloseRecordModal}
        open={archivedErrorModal.openRecordModal}
        record={archivedErrorModal.record}
      />
      <MerchantCommissionsDeleteDialog
        onClose={deleteModal.handleCloseRecordModal}
        open={deleteModal.openRecordModal}
        record={deleteModal.record}
      />
      <MerchantShowCommissionsListReferenceDialog
        onClose={() => setOpenReferenceDialog(false)}
        open={openReferenceDialog}
      />
    </ListPage>
  );
};
